import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft } from 'lucide-react';
import axios from '../interceptors/axios';
import { url } from '../utils/confserveur';
import { useClasseContext } from '../context/ClasseContext';
import { useUser } from '../context/UserContext';
import { useClasse } from '../hooks/fetch_api_hook/useClasse';
import { useGroupe } from '../hooks/fetch_api_hook/useGroupe';
import { useTheme } from '../hooks/fetch_api_hook/useTheme';
import { usePostApi } from '../hooks/post_api_hook/usePostApi';
import { useDeleteEval } from '../hooks/delete_api_hook/useDeleteEval';
import { buildEvaluationPayload, compIdParDefaut, COMPETENCES_PAR_MATIERE } from '../utils/evalPapier';
import { formatDate } from '../utils/ClasseEvaluations';
import {
    parserNotesCollees, apparierEleves, construirePayloadScores,
    construirePayloadScoresDecompose, casesEvaluees,
    attendusDuNiveau, proposerAttendus, doublonsMappages, exercicesDepuisMappages, casesDepuisMappages,
    similariteNoms, identiteEleve,
} from '../utils/collerNotes';
import EtapeCollerConfigurer from '../components/CollerNotes/EtapeCollerConfigurer';
import EtapeVerifier from '../components/CollerNotes/EtapeVerifier';
import AlertModal from '../components/general/AlertModal';

/**
 * « Coller mes notes existantes » — wizard 2 étapes qui transforme un
 * collage (Pronote, EcoleDirecte, tableur…) en évaluation en classe
 * complète : POST evaluations/ → GET evaluations/{id}/ (esfIds) →
 * mass_update_scores. La note est convertie sur l'échelle de maîtrise 1..6
 * et reportée sur chaque attendu coché ; les écarts de listes se résolvent
 * À L'ÉCRAN (étape 2), rien n'est bloquant sauf zéro élève apparié.
 */

const formatBackendError = (error) => {
    const data = error?.response?.data;
    if (!data) return 'Erreur lors de la création de l\'évaluation.';
    if (typeof data === 'string') return data;
    if (data.error) return data.error;
    return Object.entries(data)
        .map(([k, v]) => `${k} : ${Array.isArray(v) ? v.join(', ') : v}`)
        .join('\n');
};

const aujourdHuiISO = () => formatDate(new Date());

const titreParDefaut = () => {
    const d = new Date();
    const jj = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `Contrôle du ${jj}/${mm}`;
};

// Hoistée : identité stable quand l'établissement n'a pas de périodes.
const PERIODES_VIDES = [];

const CollerNotes = () => {
    const { idclasse } = useParams();
    const navigate = useNavigate();
    const { setClasseDetail, classeDetail, classeNom, groupNom, classesGroup } = useClasseContext();
    const { user, etablissementData } = useUser();

    // Même rôle que CreerEvaluation : pousser la classe/le groupe fetché dans
    // le contexte (la sidebar lit classeDetail).
    const { data: classeData } = useClasse(idclasse, classeNom === '' && 'not-enabled', false);
    const { data: groupeData } = useGroupe(idclasse + '/', groupNom === '' && 'not-enabled', false);
    const donneesClasse = classeNom ? classeData : groupeData;
    useEffect(() => {
        if (donneesClasse) setClasseDetail(donneesClasse);
    }, [donneesClasse, setClasseDetail]);

    const estGroupe = !!groupNom;
    const eleves = useMemo(() => classeDetail?.eleves || [], [classeDetail]);
    const niveau = classeNom ? classeNom[0] : (classesGroup.length > 0 && classesGroup[0] ? classesGroup[0][0] : '');
    const { data: themes } = useTheme(niveau);

    const matiere = user?.matiere === 'français' ? 'français' : 'maths';
    const competences = COMPETENCES_PAR_MATIERE[matiere];

    // ─── État du wizard ───
    const [etape, setEtape] = useState('coller'); // 'coller' | 'verifier'
    const [parseResult, setParseResult] = useState(null);
    const [titre, setTitre] = useState(titreParDefaut());
    const [dateEval, setDateEval] = useState(aujourdHuiISO());
    const [bareme, setBareme] = useState('20');
    // Chaque attendu coché porte SES compétences (comp_ids est par exercice
    // côté back) — Calculer/Lire pré-cochée par défaut sur chaque attendu.
    const [attendusSelectionnes, setAttendusSelectionnes] = useState([]);

    // Arbitrages de l'étape 2 (par ligneNo) : dissociations d'appariements
    // automatiques, résolutions manuelles (eleveId ou 'ignorer').
    const [dissocies, setDissocies] = useState(() => new Set());
    const [resolutions, setResolutions] = useState(() => new Map());

    // ─── Envoi ───
    const [phase, setPhase] = useState('saisie'); // saisie | creation | envoiScores | echecScores
    const [evalCreeeId, setEvalCreeeId] = useState(null);
    const [alerte, setAlerte] = useState(null);
    const [, doPost] = usePostApi(url + 'evaluations/');
    const { deleteEvaluation } = useDeleteEval();

    // ─── Mappage du mode décomposé : 1 ligne par colonne collée ───
    // [{colIndex, attenduId|null, compId, propose}] — `propose` marque une
    // pré-sélection automatique non confirmée (badge « proposition »).
    const [mappages, setMappages] = useState([]);
    const [propositionsFaites, setPropositionsFaites] = useState(false);
    const attendusNiveau = useMemo(() => attendusDuNiveau(themes), [themes]);

    // Échelle des compétences CHIFFRÉES : politique d'ÉTABLISSEMENT, donc un
    // seul réglage pour tout le fichier ('6' = échelle TanoEd par défaut,
    // '4' = MI/MF/MS/TBM). Mémorisée par établissement (localStorage —
    // purgé au logout, cf. ClasseContext).
    const cleEchelle = `tanoed.echelleMaitrise.${etablissementData?.id ?? 'x'}`;
    const [echelleChiffres, setEchelleChiffres] = useState(() => {
        try {
            return localStorage.getItem(cleEchelle) === '4' ? '4' : '6';
        } catch {
            return '6';
        }
    });
    const handleChangeEchelle = (e) => {
        setEchelleChiffres(e);
        try {
            localStorage.setItem(cleEchelle, e);
        } catch { /* stockage indisponible : le choix vaut pour la session */ }
    };

    // Cohérence : « sur 4 » mémorisé mais un 5/6 dans le collage → l'échelle
    // 4 est impossible, retour d'office sur 6.
    useEffect(() => {
        const max = (parseResult?.colonnesMaitrise || [])
            .reduce((m, c) => Math.max(m, c.chiffres ? (c.maxObserve || 0) : 0), 0);
        if (max > 4 && echelleChiffres === '4') setEchelleChiffres('6');
    }, [parseResult, echelleChiffres]);

    const handleColler = (texte) => {
        const resultat = parserNotesCollees(texte);
        setParseResult(resultat);
        setDissocies(new Set());
        setResolutions(new Map());
        setMappages((resultat.colonnesMaitrise || []).map((_, k) => ({
            colIndex: k,
            attenduId: null,
            compId: compIdParDefaut(matiere),
            propose: false,
        })));
        setPropositionsFaites(false);
    };

    // Pré-mappage par mots-clés dès que les attendus du niveau sont là
    // (useTheme peut répondre après le collage) — une seule fois par collage,
    // et jamais par-dessus un choix manuel.
    useEffect(() => {
        if (propositionsFaites || !attendusNiveau.length) return;
        const colonnes = parseResult?.colonnesMaitrise || [];
        if (!colonnes.length) return;
        const propositions = proposerAttendus(colonnes, attendusNiveau);
        setMappages((prev) => prev.map((m, k) => (
            m.attenduId == null && propositions[k]?.attenduId != null
                ? { ...m, attenduId: propositions[k].attenduId, propose: true }
                : m
        )));
        setPropositionsFaites(true);
    }, [propositionsFaites, attendusNiveau, parseResult]);

    const handleChangeMappage = (colIndex, patch) => {
        setMappages((prev) => prev.map((m) => (m.colIndex === colIndex
            // Le badge « proposition » ne s'efface que si le prof touche à
            // l'ATTENDU (changer l'échelle ou la compétence ne tranche rien).
            ? { ...m, ...patch, propose: 'attenduId' in patch ? false : m.propose }
            : m)));
    };

    const handleToggleAttendu = (attendu) => {
        setAttendusSelectionnes((prev) => (prev.some((a) => a.id === attendu.id)
            ? prev.filter((a) => a.id !== attendu.id)
            : [...prev, { ...attendu, compIds: [compIdParDefaut(matiere)] }]));
    };

    const handleToggleCompAttendu = (attenduId, compId) => {
        setAttendusSelectionnes((prev) => prev.map((a) => (a.id === attenduId
            ? {
                ...a,
                compIds: a.compIds.includes(compId)
                    ? a.compIds.filter((c) => c !== compId)
                    : [...a.compIds, compId],
            }
            : a)));
    };

    // Deux modes auto-détectés depuis le collage :
    //   - 'note'      : une note par élève → recopiée sur chaque case cochée ;
    //   - 'decompose' : des colonnes de maîtrise → mappage EXPLICITE, une
    //     ligne par colonne (tableau de mappage de l'étape 1).
    const mode = (parseResult?.colonnesMaitrise?.length || 0) > 0 ? 'decompose' : 'note';

    const doublons = useMemo(() => doublonsMappages(mappages), [mappages]);

    // `cases` dans l'ordre des colonnes (décomposé) ou produit attendus ×
    // compétences (note). En décomposé, une colonne laissée sans attendu n'est
    // pas importée (2026-09-29) : chaque case garde l'index de SA colonne
    // (`colIndex`), sans quoi les notes glisseraient d'une colonne.
    const cases = useMemo(() => {
        if (mode === 'decompose') return casesDepuisMappages(mappages, attendusNiveau);
        return casesEvaluees(attendusSelectionnes);
    }, [mode, mappages, attendusNiveau, attendusSelectionnes]);

    // ─── Appariement : automatique, puis application des arbitrages ───
    const classesDuGroupe = useMemo(() => {
        const noms = [...new Set(eleves.map((e) => e.classe_nom).filter(Boolean))];
        return noms.map((nom, i) => ({ id: i, nom }));
    }, [eleves]);

    const auto = useMemo(() => {
        if (!parseResult || parseResult.erreur) return { apparies: [], aResoudre: [], elevesSansNote: eleves };
        return apparierEleves(parseResult.lignes, eleves, {
            estGroupe,
            classesDuGroupe,
            classeCourante: estGroupe ? null : (classeNom || null),
        });
    }, [parseResult, eleves, estGroupe, classesDuGroupe, classeNom]);

    const etat = useMemo(() => {
        const apparies = [];
        const enAttente = [];
        const pris = new Set();

        auto.apparies.forEach((a) => {
            if (dissocies.has(a.ligne.ligneNo)) {
                enAttente.push({ ligne: a.ligne, raison: 'dissocie', ligneEnConflit: null });
            } else {
                apparies.push(a);
                pris.add(a.eleve.id);
            }
        });
        auto.aResoudre.forEach((item) => enAttente.push(item));

        // Résolutions manuelles (dans l'ordre des lignes)
        const aResoudre = [];
        enAttente
            .sort((x, y) => x.ligne.ligneNo - y.ligne.ligneNo)
            .forEach((item) => {
                const choix = resolutions.get(item.ligne.ligneNo);
                if (choix === 'ignorer') return;
                if (choix != null) {
                    const eleve = eleves.find((e) => String(e.id) === String(choix));
                    if (eleve && !pris.has(eleve.id)) {
                        apparies.push({ ligne: item.ligne, eleve, similarite: null });
                        pris.add(eleve.id);
                        return;
                    }
                }
                aResoudre.push(item);
            });

        // Candidats des selects : élèves encore libres, classés par ressemblance.
        const libres = eleves.filter((e) => !pris.has(e.id));
        const aResoudreAvecCandidats = aResoudre.map((item) => ({
            ...item,
            candidats: [...libres].sort((a, b) =>
                similariteNoms(item.ligne.nomBrut, identiteEleve(b))
                - similariteNoms(item.ligne.nomBrut, identiteEleve(a))),
        }));

        return {
            apparies: apparies.sort((a, b) => identiteEleve(a.eleve).localeCompare(identiteEleve(b.eleve))),
            aResoudre: aResoudreAvecCandidats,
            elevesSansNote: libres,
        };
    }, [auto, dissocies, resolutions, eleves]);

    const handleDissocier = (ligneNo) => {
        setResolutions((prev) => { const m = new Map(prev); m.delete(ligneNo); return m; });
        setDissocies((prev) => new Set(prev).add(ligneNo));
    };
    const handleResoudre = (ligneNo, valeur) => {
        setResolutions((prev) => new Map(prev).set(ligneNo, valeur));
    };

    // ─── Séquence de création ───
    const envoyerScores = async (evalId) => {
        setPhase('envoiScores');
        try {
            const detail = await axios.get(`${url}evaluations/${evalId}/`, { withCredentials: true });
            const scores = mode === 'decompose'
                ? construirePayloadScoresDecompose(
                    etat.apparies,
                    detail?.data?.eleves_resultats,
                    cases,
                    parseResult?.colonnesMaitrise || [],
                    mappages.map(() => echelleChiffres)
                )
                : construirePayloadScores(
                    etat.apparies,
                    detail?.data?.eleves_resultats,
                    parseFloat(bareme)
                );
            if (scores.length > 0) {
                const res = await doPost(scores, url + 'exercices_savoir_faire/mass_update_scores/');
                if (!res?.success) throw new Error('mass_update_scores');
            }
            navigate(`/home/evaluations/${idclasse}?eval=${evalId}`);
        } catch {
            setPhase('echecScores');
        }
    };

    const handleCreer = async () => {
        if (phase !== 'saisie') return;
        setPhase('creation');
        const exercices = mode === 'decompose'
            ? exercicesDepuisMappages(mappages)
            : attendusSelectionnes.map((a) => ({
                comp_ids: a.compIds,
                attendu_id: a.id,
            }));
        const payload = buildEvaluationPayload({
            titre: titre.trim(),
            date: formatDate(dateEval),
            idclasse,
            estClasse: !estGroupe,
            etablissementId: etablissementData?.id,
            exercices,
        });
        const res = await doPost(payload);
        if (!res?.success) {
            setAlerte(formatBackendError(res?.error));
            setPhase('saisie');
            return;
        }
        const evalId = res?.data?.id;
        if (!evalId) {
            setAlerte('Évaluation créée mais identifiant introuvable : vérifiez la liste des évaluations.');
            setPhase('saisie');
            return;
        }
        setEvalCreeeId(evalId);
        await envoyerScores(evalId);
    };

    const handleSupprimerEtRecommencer = async () => {
        if (evalCreeeId) await deleteEvaluation(evalCreeeId);
        setEvalCreeeId(null);
        setPhase('saisie');
    };

    // ─── Gardes ───
    const contexteAbsent = !classeDetail || eleves.length === 0;

    return (
        <div className="flex-1 px-6 py-8 lg:px-10 lg:py-10 text-black bg-gradient-to-br from-[#EAF4FB] via-[#DCEFF9] to-[#D2EBF8] min-h-full">
            <div className="w-full max-w-[1400px]">
                <button
                    type="button"
                    onClick={() => navigate(`/home/creer/${idclasse}`)}
                    className="inline-flex items-center gap-1.5 text-sm font-extrabold text-blue-600 hover:text-blue-700 mb-4"
                >
                    <ArrowLeft className="w-4 h-4" /> Créer une évaluation
                </button>
                <h1 className="text-2xl font-extrabold text-slate-800 tracking-tight">Coller mes notes existantes</h1>
                <p className="text-sm font-bold text-slate-500 mt-1 mb-6">
                    {classeNom ? `Classe ${classeNom}` : `Groupe ${groupNom}`} · l'évaluation et ses résultats sont créés d'un coup
                </p>

                {contexteAbsent ? (
                    <div className="flex items-start gap-3 px-4 py-3 rounded-xl border border-amber-300 bg-amber-50">
                        <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-amber-800">
                            <strong>Aucun élève dans cette {estGroupe ? 'sélection' : 'classe'}</strong> : importez les
                            élèves avant de coller des notes.
                        </p>
                    </div>
                ) : phase === 'echecScores' ? (
                    <div className="bg-white border-2 border-amber-300 rounded-2xl p-6 space-y-4">
                        <p className="flex items-center gap-2 text-sm font-extrabold text-amber-800">
                            <AlertTriangle className="w-5 h-5" />
                            L'évaluation « {titre} » est créée, mais les notes n'ont pas pu être envoyées.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <button
                                type="button"
                                onClick={() => envoyerScores(evalCreeeId)}
                                className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-extrabold px-5 py-2.5 rounded-xl"
                            >
                                Réessayer l'envoi des notes
                            </button>
                            <button
                                type="button"
                                onClick={handleSupprimerEtRecommencer}
                                className="bg-white border-2 border-slate-200 hover:border-red-300 hover:text-red-600 text-slate-600 text-sm font-extrabold px-5 py-2.5 rounded-xl"
                            >
                                Supprimer l'évaluation et revenir à la vérification
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white border-2 border-[#D5E6F3] rounded-2xl shadow-sm p-6">
                        {etape === 'coller' ? (
                            <EtapeCollerConfigurer
                                parseResult={parseResult}
                                onColler={handleColler}
                                onRecoller={() => setParseResult(null)}
                                titre={titre} setTitre={setTitre}
                                dateEval={dateEval} setDateEval={setDateEval}
                                bareme={bareme} setBareme={setBareme}
                                competences={competences}
                                themes={themes || []}
                                attendusSelectionnes={attendusSelectionnes}
                                onToggleAttendu={handleToggleAttendu}
                                onToggleCompAttendu={handleToggleCompAttendu}
                                mode={mode}
                                mappages={mappages}
                                onChangeMappage={handleChangeMappage}
                                attendusNiveau={attendusNiveau}
                                doublons={doublons}
                                echelleChiffres={echelleChiffres}
                                onChangeEchelle={handleChangeEchelle}
                                periodes={etablissementData?.periodes_scolaires || PERIODES_VIDES}
                                niveauClasse={niveau}
                                onVerifier={() => setEtape('verifier')}
                            />
                        ) : (
                            <EtapeVerifier
                                apparies={etat.apparies}
                                aResoudre={etat.aResoudre}
                                elevesSansNote={etat.elevesSansNote}
                                ignorees={parseResult?.ignorees || []}
                                bareme={parseFloat(bareme)}
                                mode={mode}
                                cases={cases}
                                colonnesMaitrise={parseResult?.colonnesMaitrise || []}
                                echelles={mappages.map(() => echelleChiffres)}
                                competences={competences}
                                onDissocier={handleDissocier}
                                onResoudre={handleResoudre}
                                onRetour={() => setEtape('coller')}
                                onCreer={handleCreer}
                                envoiEnCours={phase === 'creation' || phase === 'envoiScores'}
                            />
                        )}
                    </div>
                )}
            </div>
            {alerte && (
                <AlertModal
                    variant="error"
                    title="Création impossible"
                    message={alerte}
                    onClose={() => setAlerte(null)}
                />
            )}
        </div>
    );
};

export default CollerNotes;
